import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import {
  IoPeopleOutline,
  IoShieldCheckmarkOutline,
  IoPersonOutline,
  IoPersonAddOutline,
  IoPulseOutline,
  IoMailOutline,
  IoConstructOutline,
} from 'react-icons/io5';

const StatCard = ({ icon: Icon, label, value, color, hint }) => (
  <div className="glass-panel p-5 rounded-xl flex items-start gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="text-2xl text-white" />
    </div>
    <div>
      <p className="text-gray-400 text-sm">{label}</p>
      <p className="text-3xl font-bold text-white leading-tight">{value}</p>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  </div>
);

const HealthPill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${
      ok
        ? 'bg-green-500/15 text-green-300 border-green-500/30'
        : 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30'
    }`}
  >
    {label}
  </span>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, healthRes, pendingRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/system-health'),
          api.get('/admin/dashboard/clients-pending'),
        ]);
        setStats(statsRes.data);
        setHealth(healthRes.data);
        setPending(pendingRes.data || []);
      } catch (err) {
        console.error('Error cargando admin dashboard', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="skeleton h-28 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="text-3xl md:text-4xl font-display font-bold text-white">Panel de Administracion</h1>
        <p className="text-gray-400 text-sm mt-1">Funciones administrativas de usuarios y estado de plataforma.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={IoPeopleOutline} label="Usuarios Totales" value={health?.users?.total ?? 0} color="bg-blue-600" />
        <StatCard icon={IoShieldCheckmarkOutline} label="Admins" value={health?.users?.admins ?? 0} color="bg-purple-700" />
        <StatCard icon={IoPersonOutline} label="Coaches" value={health?.users?.coaches ?? 0} color="bg-marizan-red" />
        <StatCard icon={IoPersonAddOutline} label="Clientes" value={health?.users?.clients ?? 0} color="bg-green-700" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={IoConstructOutline} label="Programas" value={stats?.total_programs ?? 0} color="bg-indigo-700" />
        <StatCard icon={IoMailOutline} label="Clientes Pendientes" value={pending.length} color="bg-yellow-700" hint="esperando activacion" />
        <StatCard icon={IoPulseOutline} label="Logs Totales" value={stats?.total_logs ?? 0} color="bg-cyan-700" />
        <div className="glass-panel p-5 rounded-xl">
          <p className="text-gray-400 text-sm mb-2">Salud del Sistema</p>
          <div className="flex flex-wrap gap-2 mb-3">
            <HealthPill ok={health?.database === 'ok'} label={health?.database === 'ok' ? 'DB OK' : 'DB Error'} />
            <HealthPill ok={Boolean(health?.smtp_configured)} label={health?.smtp_configured ? 'SMTP OK' : 'SMTP Incompleto'} />
          </div>
          <p className="text-xs text-gray-500">API {health?.api_version || '-'}</p>
          <p className="text-xs text-gray-500 mt-1">{health?.server_time_utc || '-'}</p>
        </div>
      </div>

      <div className="glass-panel p-5 rounded-xl flex items-center justify-between gap-4">
        <div>
          <p className="text-white font-semibold">Gestion de usuarios</p>
          <p className="text-sm text-gray-400">Crear, actualizar rol y estado, y restablecer contraseñas.</p>
        </div>
        <Link
          to="/admin/users"
          className="bg-marizan-red hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          Abrir Usuarios
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
